import { Form } from 'react-router-dom';
import cn from 'classnames';
import { Button, ExtLinkIcon } from '../../../components';
import { useUser } from '../../../hooks/useUser';
import { NextButton } from '../common/NextButton';
import { SkipButton } from '../common/SkipButton';
import { TaskActions } from '../common/TaskActions';
import { TaskError } from '../common/TaskError';
import { TaskHeader } from '../common/TaskHeader';
import {
  useTaskActionData,
  useTaskRequirement,
  useTaskUrl,
} from '../utils/hooks';
import { TaskStatus } from '../utils/types';
import { SubmitButton } from '../common/SubmitButton';
import { useCopy } from '../../../hooks/useCopy';
import { TaskContainer } from './TaskContainer';

export const TaskRequirementNotices = () => {
  const {
    legal_entity: { notice_url: noticeUrl },
  } = useUser();
  const {
    status,
    source: { description, location },
  } = useTaskRequirement();
  const isDone = status === TaskStatus.Done;
  const actionData = useTaskActionData();
  const taskUrl = useTaskUrl();
  const noticeUrlWithRegion = `${noticeUrl}?region=${location.name}`;
  const [handleCopy, isCopied] = useCopy(noticeUrlWithRegion);

  return (
    <TaskContainer>
      <TaskHeader />

      <div>
        <div className={cn({ 'mb-2': actionData?.errors?.submit })}>
          {!isDone && <TaskError>{actionData?.errors?.submit}</TaskError>}
        </div>

        <p>{description}</p>
      </div>

      {noticeUrl && isDone && (
        <>
          <p>
            All required notices and posters for {location.name} are now
            available on your{' '}
            <a
              href={noticeUrlWithRegion}
              target="_blank"
              className="text-teal-700 underline"
              rel="noreferrer"
              aria-label="Notice Board – opens in new tab"
            >
              Notice Board
              <ExtLinkIcon className="mb-px ml-[0.25em] inline-block size-4" />
            </a>
            . Add these notices to an internal wiki or document that is readily
            accessible to all remote employees and notify them of where to find
            it. Updates will happen automatically for all regions you add.
          </p>
          <p>
            If you have a physical office in {location.name}, print the notices
            and post them in an area that is accessible and visible to all
            employees.
          </p>
        </>
      )}

      <TaskActions>
        {!isDone && <SkipButton />}

        {isDone ? (
          <>
            <Button onClick={handleCopy} size="xlarge" variant="secondary">
              {isCopied ? 'Link Copied' : 'Copy Link'}
            </Button>
            <NextButton>Next</NextButton>
          </>
        ) : (
          <Form method="POST" action={taskUrl}>
            <input type="hidden" name="locationId" value={location.code} />

            <SubmitButton intent="resolve-notices">
              Generate Notices & Posters
            </SubmitButton>
          </Form>
        )}
      </TaskActions>
    </TaskContainer>
  );
};
